import CommonComponentLoader from "components/Loaders/CommonComponentLoader";
import ComponentCircleLoader from "components/Loaders/ComponentCircleLoader";
import StatusWithDot from "components/StatusWithDot/StatusWithDot";
import { LoadingSpinner } from "custom_modules/AuthNew/shared/authStyles";
import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import moment from "moment-timezone";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";



import RightSlidingPanel from "../../../components/RightSlidingPanel/RightSlidingPanel";
import { CloseButton } from "../../../styledComponents/buttons";
import { CommonFlex, CommonGrid, CommonText } from "../../../styledComponents/common";
import { globalDateFormat } from "../../../utils/helper";


const PanelContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 48px 24px 20px;
  gap: 28px;
  width: 547px;
  min-height: 991px;
  background: #ffffff;
  border-left: 1px solid #b4c6f9;
  box-shadow: -4px 0px 50px rgba(34, 102, 239, 0.12);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: auto;
`;

const AssigneeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 16px;
  width: auto;
`;

const AssigneeGrid = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AssigneeDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled(CommonText)`
  font-weight: 700;
  font-size: 14px;
`;

const ConflictSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  align-items: flex-start;
`;

const TimeLabel = styled(CommonText)`
  width: auto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #353a46;
`;

const TimeValue = styled(CommonText)`
  width: 499px;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000e2a;
`;

const TableHeaderGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr 1fr"};
  grid-column-gap: 12px;
  padding: 10px 12px;
  flex-direction: row;
  align-items: center;
  background: #d4eefd;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 4px 4px 0 0;
`;

const TableSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const TableRowGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr 1fr"};
  grid-column-gap: 12px;
  padding: 10px 12px;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  padding: 10px 12px;
  border-bottom: ${({ hideBorder }) =>
    hideBorder ? "none" : "1px solid #e9f0fe"};
`;

const TimeDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 14px;
  color: #353a46;
  line-height: 20px;
`;

const TimeLabelText = styled(CommonText)`
  margin: 0;
  font-weight: 400;
  color: #353a46;
`;

const TimeValueText = styled(CommonText)`
  margin: 0;
  font-weight: 400;
  color: #353a46;
`;

const JobConflictSidePanel = ({
  selectedOrganization,
  isOpen,
  closePanel,
  assignee,
  jobConflictList,
  jobConflictListIsLoading,
  startDate,
  startTime,
  endTime,
  endDate,
  isRecurring,
  getJobConflictList,
}) => {
  const { t } = useTranslation();
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();

  const renderConflictDateTime = row => {
    if (!row.start_time || !row.end_time) {
      return row.time || "-";
    }

    return (
      <TimeDetails>
        <TimeLabelText fontWeight="600">
          {globalDateFormat(row.start_time, orgTimezone)}
        </TimeLabelText>
        <TimeValueText name>
          {row.is_anytime === true
            ? t("anytime_of_the_day")
            : `${moment(row.start_time + "Z")
                .tz(orgTimezone)
                .format("h:mm a")} - ${moment(row.end_time + "Z")
                .tz(orgTimezone)
                .format("h:mm a")}`}
        </TimeValueText>
      </TimeDetails>
    );
  };
  const formatTimeValue = value => {
    if (!value) return "";
    const parsed = moment(value, ["HH:mm:ss", "HH:mm"]);
    return parsed.isValid() ? parsed.format("h:mma") : value;
  };

  const conflictTimeText = () => {
    if (!startDate || !startTime || !endTime) {
      return "No schedule available yet.";
    }

    const startDateLabel = startDate.format("D MMM");
    const startTimeLabel = formatTimeValue(startTime);
    const endTimeLabel = formatTimeValue(endTime);

    if (isRecurring && endDate) {
      const endDateLabel = endDate.format("D MMM YYYY");
      return `${startDateLabel} ${startTimeLabel} to ${endDateLabel} ${endTimeLabel}`;
    }

    return `${startDateLabel} ${startTimeLabel} to ${startDateLabel} ${endTimeLabel}`;
  };

  const conflictRowsData =
    jobConflictList && jobConflictList.length > 0
      ? jobConflictList
      : assignee?.conflicts || [];

  useEffect(() => {
    if (
      isOpen &&
      selectedOrganization?.id &&
      assignee?.id &&
      startDate &&
      startTime &&
      endTime
    ) {
      const start_time = `${startDate.format("YYYY-MM-DD")}T${startTime}`;
      const end_time = `${isRecurring && endDate ? endDate.format("YYYY-MM-DD") : startDate.format("YYYY-MM-DD")}T${endTime}`;

      getJobConflictList({
        organization_id: selectedOrganization.id,
        assignee_id: assignee.id,
        start_time,
        end_time,
        paginate: false,
      });
    }
  }, [
    isOpen,
    selectedOrganization,
    assignee,
    startDate,
    startTime,
    endTime,
    getJobConflictList,
  ]);

  return (
    <RightSlidingPanel
      isOpen={isOpen}
      closePanel={closePanel}
      width="547px"
      contentZIndex={2000}
      overlayZIndex={1500}
    >
      <PanelContainer disableResponsive column="1fr" gap="28px">
        <HeaderRow disableResponsive>
          <CommonText fontWeight="700" fontSize="18px">
            Overlapping details
          </CommonText>
          <CloseButton type="button" onClick={closePanel} aria-label="Close">
            ×
          </CloseButton>
        </HeaderRow>

        <AssigneeSection>
          <SectionTitle>Assignee</SectionTitle>
          {assignee ? (
            <AssigneeGrid columns="40px auto" gap="10px">
              <CommImageNameComp
                imgSource={assignee.avatar || assignee.image}
                name={assignee.name || "Unknown"}
                size="40px"
                showAddDesignation={false}
                designation={assignee.designation}
              />
              {/* <AssigneeDetails>
                <CommonText fontWeight="700" fontSize="14px">
                  {assignee.name || "Unknown"}
                </CommonText>
                {assignee.designation && (
                  <CommonText fontWeight="400" fontSize="12px" color="#353A46">
                    {assignee.designation}
                  </CommonText>
                )}
                <CommonText fontWeight="400" fontSize="12px" color="#353A46">
                  {assignee.email || assignee.username || ""}
                </CommonText>
              </AssigneeDetails> */}
            </AssigneeGrid>
          ) : (
            <CommonText>No conflicting assignee selected.</CommonText>
          )}
        </AssigneeSection>

        <ConflictSection>
          <CommonText fontWeight="500" fontSize="14px" color="#353A46">
            Time conflict in between
          </CommonText>
          <CommonText fontWeight="700" fontSize="14px" color="#000E2A">
            {conflictTimeText()}
          </CommonText>
        </ConflictSection>

        <TableSection>
          <TableHeaderGrid gap="0px">
            <CommonText fontWeight="500" fontSize="14px" color="#0e0e0e">
              Task name
            </CommonText>
            <CommonText fontWeight="500" fontSize="14px" color="#000E2A">
              Date & time
            </CommonText>
            <CommonText fontWeight="500" fontSize="14px" color="#000E2A">
              Status
            </CommonText>
          </TableHeaderGrid>

          {jobConflictListIsLoading ? (
            <ComponentCircleLoader size={20} />
          ) : conflictRowsData.length > 0 ? (
            conflictRowsData.map((row, index) => (
              <TableRowGrid
                key={index}
                gap="0px"
                hideBorder={index === conflictRowsData.length - 1}
              >
                <CommonText
                  capitalize
                  fontWeight="700"
                  fontSize="14px"
                  color="#000E2A"
                >
                  {row.name}
                </CommonText>
                {renderConflictDateTime(row)}
                <StatusWithDot status={row.status} />
              </TableRowGrid>
            ))
          ) : (
            <TableRowGrid gap="0px" hideBorder>
              <CommonText fontWeight="400" fontSize="14px" color="#353A46">
                No conflicts found.
              </CommonText>
            </TableRowGrid>
          )}
        </TableSection>
      </PanelContainer>
    </RightSlidingPanel>
  );
};

export default JobConflictSidePanel;